import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { usePlatform } from '../../context/PlatformContext';
import { aftertidePromotionalBanners, aftertideFeatureCards } from '../../data/aftertideHomePageData';
// Feature cards from "What is LitLaunch?" section
const featureCards = [{
  title: 'Trope-Based Discovery',
  description: 'Find your next read based on the story elements you love, not just genres.',
  bgColor: 'bg-gradient-to-r from-indigo-500 to-violet-500',
  textColor: 'text-white',
  link: '/tropes?featured=popular-tropes'
}, {
  title: 'Indie Author Focused',
  description: 'Connect directly with independent authors and support their creative journeys.',
  bgColor: 'bg-gradient-to-r from-indigo-500 to-blue-500',
  textColor: 'text-white',
  link: '/authors?featured=indie-spotlight'
}, {
  title: 'All-in-One Platform',
  description: 'Discover books, audiobooks, merchandise, and events all in one seamless experience.',
  bgColor: 'bg-gradient-to-r from-violet-500 to-fuchsia-500',
  textColor: 'text-white',
  link: '/browse?featured=platform-picks'
}];
// Placeholder promotional banners (replace with your actual banner data)
const promotionalBanners = [{
  id: 1,
  title: 'Summer Reading Sale',
  description: 'Get 20% off all paperbacks through August',
  bgColor: 'bg-gradient-to-r from-blue-500 to-cyan-500',
  textColor: 'text-white',
  link: '/browse?featured=summer-sale'
}, {
  id: 2,
  title: 'New Author Spotlight',
  description: "Discover this month's featured indie authors",
  bgColor: 'bg-gradient-to-r from-amber-500 to-orange-500',
  textColor: 'text-white',
  link: '/authors?featured=author-spotlight'
}, {
  id: 3,
  title: 'Audiobook Launch',
  description: 'New audiobooks added every week',
  bgColor: 'bg-gradient-to-r from-emerald-500 to-teal-500',
  textColor: 'text-white',
  link: '/audiobooks?featured=new-launches'
}, {
  id: 4,
  title: 'Join Our Newsletter',
  description: 'Get new release alerts and exclusive content',
  bgColor: 'bg-gradient-to-r from-indigo-500 to-pink-500',
  textColor: 'text-white'
}];
const CustomPromotionalBannerCarousel = () => {
  const navigate = useNavigate();
  const {
    isAfterTide
  } = usePlatform();
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');
  // Select data based on platform
  const activeBanners = isAfterTide ? aftertidePromotionalBanners : promotionalBanners;
  const activeFeatureCards = isAfterTide ? aftertideFeatureCards : featureCards;
  // Combine promotional banners and feature cards
  const combinedBanners = [...activeBanners];
  activeFeatureCards.forEach((card, index) => {
    const insertPosition = 2 + index * 2;
    if (insertPosition <= combinedBanners.length) {
      combinedBanners.splice(insertPosition, 0, {
        id: `feature-${index}`,
        isFeatureCard: true,
        ...card
      });
    }
  });
  const totalBanners = combinedBanners.length;
  const goTo = useCallback((newIndex: number, dir: 'next' | 'prev') => {
    if (isTransitioning) return;
    setDirection(dir);
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentBannerIndex(newIndex);
      setTimeout(() => setIsTransitioning(false), 50);
    }, 200);
  }, [isTransitioning]);
  // Reset index when platform changes
  useEffect(() => {
    setCurrentBannerIndex(0);
  }, [isAfterTide]);
  // Auto-rotate banners
  useEffect(() => {
    const interval = setInterval(() => {
      const nextIndex = (currentBannerIndex + 1) % totalBanners;
      goTo(nextIndex, 'next');
    }, 5000);
    return () => clearInterval(interval);
  }, [totalBanners, currentBannerIndex, goTo]);
  const goToPrevBanner = () => {
    const prevIndex = (currentBannerIndex - 1 + totalBanners) % totalBanners;
    goTo(prevIndex, 'prev');
  };
  const goToNextBanner = () => {
    const nextIndex = (currentBannerIndex + 1) % totalBanners;
    goTo(nextIndex, 'next');
  };
  const currentBanner = combinedBanners[currentBannerIndex];
  const handleBannerClick = () => {
    if (currentBanner.link) {
      navigate(currentBanner.link);
    }
  };
  // AfterTide-specific nav button styling
  const navBtnClass = isAfterTide ? 'bg-[#e2b04a]/10 hover:bg-[#e2b04a]/20 backdrop-blur-sm rounded-full p-0.5 z-10 text-[#e2b04a] transition-colors' : 'bg-black/10 hover:bg-black/20 backdrop-blur-sm rounded-full p-0.5 z-10 text-gray-700 transition-colors';
  const dotActiveClass = isAfterTide ? 'w-3 h-1 bg-[#e2b04a]/60' : 'w-3 h-1 bg-black/40';
  const dotInactiveClass = isAfterTide ? 'w-1 h-1 bg-[#e2b04a]/20 hover:bg-[#e2b04a]/30' : 'w-1 h-1 bg-black/15 hover:bg-black/25';
  return <div className="relative w-full overflow-hidden" data-id="element-3705" data-mp="khitn0kiuwuv">
      {/* Banner content with fade transition */}
      <div className={`relative w-full transition-opacity duration-300 ease-in-out ${currentBanner.link ? 'cursor-pointer' : ''}`} style={{
      opacity: isTransitioning ? 0 : 1
    }} onClick={handleBannerClick} role={currentBanner.link ? 'link' : undefined} tabIndex={currentBanner.link ? 0 : undefined} onKeyDown={e => {
      if (currentBanner.link && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        handleBannerClick();
      }
    }} data-id="element-3706" data-mp="phewuxleak9d">
        {currentBanner.isFeatureCard ? <div className={`w-full ${currentBanner.bgColor} py-2.5 px-10 sm:px-12`} data-id="element-3707" data-mp="3sx2h1iyihl20">
            <div className="max-w-7xl mx-auto flex items-center justify-center" data-id="element-3708" data-mp="3exuhgoyfsaxa">
              <div className={`${currentBanner.textColor} text-center min-w-0`} data-id="element-3709" data-mp="3j07kh5lstuqd">
                <p className="hidden sm:block text-sm" data-id="element-3710" data-mp="1pgyw4re4pr4i">
                  <span className="font-semibold" data-id="element-3711" data-mp="1pijqafwj781u">{currentBanner.title}</span>
                  <span className="mx-1.5 opacity-60" data-id="element-3712" data-mp="1kp7dr454mrpg">·</span>
                  <span className="opacity-90" data-id="element-3713" data-mp="1fr5kxrhwfe0s">
                    {currentBanner.description}
                  </span>
                </p>
                <div className="sm:hidden" data-id="element-3714" data-mp="1at40p38ql8nq">
                  <h3 className="text-sm font-semibold leading-tight" data-id="element-3715" data-mp="1aq82ddsz8hyr">
                    {currentBanner.title}
                  </h3>
                  <p className="text-xs opacity-90 leading-tight" data-id="element-3716" data-mp="23g88ffz7s137">
                    {currentBanner.description}
                  </p>
                </div>
              </div>
            </div>
          </div> : <div className={`w-full ${currentBanner.bgColor} py-2.5 px-10 sm:px-12`} data-id="element-3717" data-mp="1ufa3mvgybpjk">
            <div className="max-w-7xl mx-auto flex items-center justify-center" data-id="element-3718" data-mp="n41nc7l4y1z1">
              <div className={`${currentBanner.textColor} text-center min-w-0`} data-id="element-3719" data-mp="izttxx3qfxuh">
                <p className="hidden sm:block text-sm" data-id="element-3720" data-mp="2uorx4m3k0ipu">
                  <span className="font-semibold" data-id="element-3721" data-mp="2um5jc4q2l6z6">{currentBanner.title}</span>
                  <span className="mx-1.5 opacity-60" data-id="element-3722" data-mp="2yqd6hxgvqeva">—</span>
                  <span className="opacity-90" data-id="element-3723" data-mp="33oetuulc90ck">
                    {currentBanner.description}
                  </span>
                </p>
                <div className="sm:hidden" data-id="element-3724" data-mp="38h7y7hcwa6o4">
                  <h3 className="text-sm font-semibold leading-tight" data-id="element-3725" data-mp="3dezwqu81or1a">
                    {currentBanner.title}
                  </h3>
                  <p className="text-xs opacity-90 leading-tight" data-id="element-3726" data-mp="3ichx3w7zxxxo">
                    {currentBanner.description}
                  </p>
                </div>
              </div>
            </div>
          </div>}
      </div>

      {/* Navigation buttons */}
      <button onClick={goToPrevBanner} className={`absolute left-1.5 top-1/2 -translate-y-1/2 ${navBtnClass}`} aria-label="Previous banner" data-id="element-3727" data-mp="1s1n44hvnd03j">
        <ChevronLeftIcon className="h-4 w-4" data-id="element-3728" data-mp="1x2b4vv9ogvay" />
      </button>
      <button onClick={goToNextBanner} className={`absolute right-1.5 top-1/2 -translate-y-1/2 ${navBtnClass}`} aria-label="Next banner" data-id="element-3729" data-mp="2rbc7dc701gk">
        <ChevronRightIcon className="h-4 w-4" data-id="element-3730" data-mp="ciyykztw6sb6" />
      </button>

      {/* Compact dot indicators */}
      <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 flex space-x-1" data-id="element-3731" data-mp="7iuhb7l5ttjw">
        {combinedBanners.map((_, index) => <button key={index} onClick={() => goTo(index, index > currentBannerIndex ? 'next' : 'prev')} className={`rounded-full transition-all duration-300 ${currentBannerIndex === index ? dotActiveClass : dotInactiveClass}`} aria-label={`Go to banner ${index + 1}`} data-id="element-3732" data-mp="3fzxp9sru9eym" data-mpi={index} />)}
      </div>
    </div>;
};
export default CustomPromotionalBannerCarousel;